import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Upload, FileJson, CheckCircle, Copy, Code } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { fadeInUp } from "@/config/animations";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SAMPLE_CONFIGS = {
  crewai: JSON.stringify({
    agents: [
      { role: "researcher", goal: "Find comprehensive data on the topic", llm: "gpt-4o" },
      { role: "writer", goal: "Write a detailed report", llm: "claude-sonnet" },
      { role: "reviewer", goal: "Review for accuracy and quality", llm: "gpt-4o-mini" },
    ],
    tasks: [
      { description: "Research the topic thoroughly", agent: "researcher", expected_output: "Research summary" },
      { description: "Write a comprehensive report", agent: "writer", expected_output: "Full report" },
      { description: "Review the report for quality", agent: "reviewer", expected_output: "Review notes" },
    ],
    process: "sequential",
  }, null, 2),
  autogen: JSON.stringify({
    agents: [
      { name: "assistant", llm_config: { model: "gpt-4o" } },
      { name: "reviewer", llm_config: { model: "claude-sonnet" } },
    ],
    group_chat: { agents: ["assistant", "reviewer"], max_round: 10, speaker_selection_method: "auto" },
  }, null, 2),
  langgraph: JSON.stringify({
    nodes: [
      { name: "research", type: "llm_node", model: "gpt-4o", estimated_tokens: 5000 },
      { name: "analyze", type: "llm_node", model: "gemini-3-pro", estimated_tokens: 3000 },
      { name: "write", type: "llm_node", model: "claude-sonnet", estimated_tokens: 8000 },
    ],
    edges: [
      { from: "research", to: "analyze" },
      { from: "analyze", to: "write" },
    ],
  }, null, 2),
  manus: JSON.stringify({
    steps: [
      { name: "plan", agent: "planner", model: "gpt-4o", estimated_tokens: 3000 },
      { name: "execute", agent: "executor", model: "claude-sonnet", estimated_tokens: 8000 },
      { name: "validate", agent: "validator", model: "gpt-4o-mini", estimated_tokens: 2000 },
    ],
    memory: { type: "shared", max_tokens: 50000 },
    context_passing: true,
  }, null, 2),
};

const PLATFORM_LABELS = {
  crewai: { name: "CrewAI", color: "#10b981" },
  autogen: { name: "AutoGen", color: "#3b82f6" },
  langgraph: { name: "LangGraph", color: "#f59e0b" },
  manus: { name: "Manus", color: "#8b5cf6" },
};

export default function WorkflowImport({ onImported }) {
  const [open, setOpen] = useState(false);
  const [platform, setPlatform] = useState("crewai");
  const [configJson, setConfigJson] = useState(SAMPLE_CONFIGS.crewai);
  const [loading, setLoading] = useState(false);
  const [sdkExamples, setSdkExamples] = useState([]);
  const [selectedSdk, setSelectedSdk] = useState(null);
  const [sdkCode, setSdkCode] = useState("");

  useEffect(() => {
    axios.get(`${API}/mcp/sdk`).then(r => setSdkExamples(r.data)).catch(() => {});
  }, []);

  const handlePlatformChange = (val) => {
    setPlatform(val);
    setConfigJson(SAMPLE_CONFIGS[val] || "{}");
  };

  const handleImport = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/workflows/import`, {
        platform,
        config_json: configJson,
      });
      toast.success(`Imported ${res.data.tasks?.length || 0} tasks from ${platform}`);
      setOpen(false);
      onImported?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Import failed");
    } finally {
      setLoading(false);
    }
  };

  const loadSdk = async (id) => {
    try {
      const res = await axios.get(`${API}/mcp/sdk/${id}`);
      setSdkCode(res.data.code);
      setSelectedSdk(id);
    } catch {
      toast.error("Failed to load SDK example");
    }
  };

  const copyCode = () => {
    navigator.clipboard.writeText(sdkCode);
    toast.success("Copied to clipboard");
  };

  return (
    <>
      <Button onClick={() => setOpen(true)} variant="outline" size="sm" data-testid="import-workflow-btn" data-automation-id="import-workflow-btn">
        <Upload className="w-3 h-3 mr-1" /> Import Workflow
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[var(--qn-card)] border-[var(--qn-border)] max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Import Platform Workflow</DialogTitle>
            <DialogDescription>Paste your platform config JSON to import as a QNNEAL workflow</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Platform Selection */}
            <div>
              <label className="text-base font-medium mb-1 block">Platform</label>
              <Select value={platform} onValueChange={handlePlatformChange}>
                <SelectTrigger className="bg-[var(--qn-background)] border-[var(--qn-border)]" data-testid="import-platform-select" data-automation-id="import-platform-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(PLATFORM_LABELS).map(([key, val]) => (
                    <SelectItem key={key} value={key}>
                      <span className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full" style={{ backgroundColor: val.color }} />
                        {val.name}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Config JSON */}
            <div>
              <label className="text-base font-medium mb-1 block">Configuration JSON</label>
              <Textarea
                value={configJson}
                onChange={(e) => setConfigJson(e.target.value)}
                className="bg-[var(--qn-background)] border-[var(--qn-border)] font-mono text-sm min-h-[200px]"
                data-testid="import-config-textarea" data-automation-id="import-config-textarea"
              />
            </div>

            {/* SDK Examples */}
            {sdkExamples.length > 0 && (
              <div>
                <label className="text-base font-medium mb-2 block">SDK Client Examples</label>
                <div className="flex gap-2 flex-wrap mb-2">
                  {sdkExamples.map(sdk => (
                    <Badge
                      key={sdk.id}
                      className={`cursor-pointer text-sm transition-all duration-200 ${selectedSdk === sdk.id ? "bg-[var(--qn-primary)] text-white border-[var(--qn-primary)]" : "bg-white/[0.08] text-slate-300 border border-white/[0.15] hover:bg-white/[0.14] hover:text-slate-200 hover:border-white/[0.25]"}`}
                      onClick={() => loadSdk(sdk.id)}
                      data-testid={`sdk-badge-${sdk.id}`}
                    >
                      <Code className="w-3 h-3 mr-1" /> {sdk.name}
                    </Badge>
                  ))}
                </div>
                {sdkCode && (
                  <div className="relative">
                    <Button size="sm" variant="ghost" className="absolute top-2 right-2 z-10" onClick={copyCode} data-testid="copy-sdk-btn" data-automation-id="copy-sdk-btn">
                      <Copy className="w-3 h-3" />
                    </Button>
                    <pre className="bg-[var(--qn-background)] rounded-lg p-3 text-xs font-mono overflow-auto max-h-[200px] text-[var(--qn-foreground)]">
                      {sdkCode}
                    </pre>
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={handleImport} disabled={loading} className="btn-primary" data-testid="confirm-import-btn" data-automation-id="confirm-import-btn">
              {loading ? "Importing..." : <><Upload className="w-4 h-4 mr-2" /> Import & Create Workflow</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
